"use client";

import { Check } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { CopyLinkIcon } from "./share-brand-icons";

export interface ShareOption {
 name: string;
 icon: React.ReactNode;
 action: () => void;
}

interface ShareDialogProps {
 shareUrl: string;
 options: ReadonlyArray<ShareOption>;
 copiedLink: boolean;
 onCopy: () => void;
 onClose: () => void;
}

export function ShareDialog({
 shareUrl,
 options,
 copiedLink,
 onCopy,
 onClose,
}: ShareDialogProps) {
 return (
  <Dialog open onOpenChange={(open) => !open && onClose()}>
   <DialogContent className="rounded-2xl p-6 sm:p-6">
    <DialogTitle className="mb-2 text-xl font-semibold text-foreground">
     Share this listing
    </DialogTitle>
    <ShareOptionList options={options} copiedLink={copiedLink} />
    <ShareLinkPanel shareUrl={shareUrl} copiedLink={copiedLink} onCopy={onCopy} />
   </DialogContent>
  </Dialog>
 );
}

function ShareLinkPanel({
 shareUrl,
 copiedLink,
 onCopy,
}: Pick<ShareDialogProps, "shareUrl" | "copiedLink" | "onCopy">) {
 return (
  <div className="mt-2 flex items-center gap-3 rounded-lg border border-border bg-muted p-4">
   <div className="min-w-0 flex-1">
    <p className="text-xs font-medium text-muted-foreground">
     Link
    </p>
    <p className="mt-1 truncate text-sm text-foreground">
     {shareUrl}
    </p>
   </div>
   <Button
    type="button"
    variant="outline"
    onClick={onCopy}
    aria-label="Copy link"
   >
    {copiedLink ? (
     <span className="flex items-center gap-2 text-success-strong">
      <Check className="size-4" />
      Copied
     </span>
    ) : (
     <span className="flex items-center gap-2">
      <CopyLinkIcon />
      Copy
     </span>
    )}
   </Button>
  </div>
 );
}

function ShareOptionList({
 options,
 copiedLink,
}: {
 options: ReadonlyArray<ShareOption>;
 copiedLink: boolean;
}) {
 return (
  <div className="space-y-2">
   {options.map((option) => (
    <Button
     key={option.name}
     type="button"
     variant="ghost"
     size="auto"
     onClick={option.action}
     className={cn(
      "text-left w-full justify-start gap-4 p-3 text-base",
      copiedLink && option.name === "Link copied!" && "bg-success-bg",
     )}
    >
     <span className="shrink-0 text-foreground">{option.icon}</span>
     <span className="font-medium text-foreground">{option.name}</span>
    </Button>
   ))}
  </div>
 );
}
